/* eslint-disable no-unreachable-loop */
import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Alert } from '@mui/material';
import { AlertTriangle } from 'react-feather';
import Header from '@/components/Header/Header';
import Link from '@/components/Link/Link';
import styles from './NationalModifiers.module.css';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useNationalModifiers, useUpdateNationalModifiersLock } from '@/services/nationalModifiersService';
import { NationalModifiersDetails, NationalModifiersSelection } from '@/types/nationalModifiers.types';
import { SwitchCell } from './internal/SwitchCell';
import Button from '@/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { useNationalModifiersSaveState } from '@/hooks/useSaveButtonState';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';

const NationalModifiers: FC = () => {
  const { t } = useTranslation();
  const { data: dataNationalModifiers, isLoading, isError, refetch } = useNationalModifiers(true, 'modifier');
  const [data, setData] = useState(dataNationalModifiers?.data || []);
  const [originalData, setOriginalData] = useState(dataNationalModifiers?.data || []);
  const { mutate: updateIsLockForNationalModifiers } = useUpdateNationalModifiersLock();

  // Use optimized hooks
  const debouncedUpdate = useDebouncedUpdate(setData, 100);
  const isIdenticalData = useNationalModifiersSaveState(originalData, data);

  const columns = useMemo(
    () => [
      {
        id: 'isVisible',
        cell: SwitchCell,
        header: t('modifiers.table.visibility'),
        accessorKey: 'isVisible',
        meta: {
          disableFilter: true,
          tooltipText: 'Use the Visibility setting to choose which modifiers franchisees can view.',
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'isMandatory',
        cell: SwitchCell,
        header: t('modifiers.table.mandatory'),
        accessorKey: 'isMandatory',
        meta: {
          disableFilter: true,
          tooltipText: 'The Mandatory lock keeps Brand Standard modifiers from being removed.',
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'isLock',
        cell: SwitchCell,
        header: t('modifiers.table.lockPrice'),
        accessorKey: 'isLock',
        meta: {
          disableFilter: true,
          tooltipText: 'The Price Lock stops users from editing modifier prices.',
        },
        enableSorting: true,
        size: 50,
      },
      {
        id: 'isZeroPriced',
        cell: SwitchCell,
        header: t('modifiers.table.zeroPrice'),
        accessorKey: 'isZeroPriced',
        meta: {
          disableFilter: true,
          tooltipText: 'When toggled on, the price for the modifier is required to be $0.00.',
        },
        enableSorting: true,
        size: 50,
      },
      {
        accessorFn: (originalRow: NationalModifiersDetails) => originalRow.slotName || '-',
        header: t('common.slot'),
        enableSorting: true,
        size: 220,
      },
      {
        accessorFn: (originalRow: NationalModifiersDetails) => originalRow.modifierName || '-',
        header: t('modifiers.table.modifier'),
        enableSorting: true,
        size: 240,
      },
      {
        accessorFn: (originalRow: NationalModifiersDetails) => {
          const weightName = originalRow.modifierWeightName;
          // Show the weight name unless it's "none" or empty
          if (!weightName || weightName.toLowerCase() === 'none') {
            return '-';
          }
          return weightName;
        },
        header: t('modifiers.table.modifierWeight'),
        enableSorting: true,
        size: 300,
      },
    ],
    [t],
  );

  if (isError) {
    showToast({
      label: t('modifiers.errorFetching'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingNationalModifiers',
    });
  }

  useEffect(() => {
    if (dataNationalModifiers?.data) {
      setData(dataNationalModifiers.data);
      setOriginalData(dataNationalModifiers.data);
    }
  }, [dataNationalModifiers]);

  const onButtonClick = useCallback(() => {
    // Flush any pending debounced updates before saving
    const updateFn = debouncedUpdate as typeof debouncedUpdate & { flush?: () => void };

    if (updateFn.flush) {
      flushSync(() => {
        updateFn.flush!();
      });
    }

    // Build the update payload with the current data state
    const nationalModifiersToUpdateSelection: NationalModifiersSelection[] = [];

    originalData.forEach((modifier) => {
      const resultObject = data.find((modifierInternal) => modifierInternal.id === modifier.id);

      if (!resultObject) return;

      // Normalize to booleans for comparison
      const resultIsLock = resultObject.isLock != null ? Boolean(resultObject.isLock) : false;
      const originalIsLock = modifier.isLock != null ? Boolean(modifier.isLock) : false;

      const resultIsVisible = resultObject.isVisible != null ? Boolean(resultObject.isVisible) : false;
      const originalIsVisible = modifier.isVisible != null ? Boolean(modifier.isVisible) : false;

      const resultIsMandatory = resultObject.isMandatory != null ? Boolean(resultObject.isMandatory) : false;
      const originalIsMandatory = modifier.isMandatory != null ? Boolean(modifier.isMandatory) : false;

      const resultIsZeroPriced = resultObject.isZeroPriced != null ? Boolean(resultObject.isZeroPriced) : false;
      const originalIsZeroPriced = modifier.isZeroPriced != null ? Boolean(modifier.isZeroPriced) : false;

      if (
        resultIsLock !== originalIsLock ||
        resultIsVisible !== originalIsVisible ||
        resultIsMandatory !== originalIsMandatory ||
        resultIsZeroPriced !== originalIsZeroPriced
      ) {
        // Ensure we always send proper boolean values (never null/undefined)
        const nms: NationalModifiersSelection = {
          id: resultObject.id,
          isLock: resultIsLock,
          isVisible: resultIsVisible,
          isMandatory: resultIsMandatory,
          isZeroPriced: resultIsZeroPriced,
        };
        nationalModifiersToUpdateSelection.push(nms);
      }
    });

    if (nationalModifiersToUpdateSelection.length > 0) {
      updateIsLockForNationalModifiers(nationalModifiersToUpdateSelection, {
        onSuccess: () => {
          showToast({
            id: 'editNationalModifiersLock',
            label: t('modifiers.lockStatusUpdated'),
          });

          refetch();
        },
        onError: () => {
          showToast({
            id: 'errorUpdatingNationalModifiersLock',
            label: t('modifiers.errorUpdatingLockStatus'),
            toastType: ToastTypes.Error,
          });
        },
      });
    }
  }, [originalData, data, debouncedUpdate, updateIsLockForNationalModifiers, t, refetch]);

  return (
    <>
      <Header title="National Modifiers" breadcrumb={{ to: '/', label: 'Back to Home' }} />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          All Modifiers
          <Button disabled={isIdenticalData} onClick={onButtonClick}>
            Save Changes
          </Button>
        </div>
        <Alert
          data-testid="managed-in-named-price-modifiers-banner"
          icon={<AlertTriangle size={20} color="#E6740A" />}
          severity="warning"
          className={styles.banner}
          sx={{
            backgroundColor: '#FAE4D0',
            color: '#3D3D3D',
            border: '1px solid #E6740A',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 400,
            alignItems: 'center',
            padding: '11px 16px',
            minHeight: '42px',
            '& .MuiAlert-icon': {
              color: '#E6740A',
              padding: 0,
              marginRight: '12px',
              opacity: 1,
              alignItems: 'center',
            },
            '& .MuiAlert-message': {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <div>
            {t('modifiers.managedInNamedPriceModifiers')}
            <Link to="/namedpricemodifiers">{t('modifiers.namedPriceModifiersLink')}</Link>
          </div>
        </Alert>
        <TableCard
          height="calc(75vh - 100px)"
          isLoading={isLoading}
          data={data}
          meta={{
            columns,
            data,
            updateData: debouncedUpdate,
          }}
          columns={columns}
          enableFilters
        />
      </div>
    </>
  );
};

export default NationalModifiers;
